import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star, ShieldCheck, Leaf } from "lucide-react";
import { Reveal, Overline, Sparkle, EASE } from "@/components/Reveal";

const IMG = {
  hero: "https://images.unsplash.com/photo-1715593949273-09009558300a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBleGVjdXRpdmUlMjBvZmZpY2UlMjBpbnRlcmlvciUyMGx1eHVyeSUyMHdhcm0lMjBzdW5saWdodHxlbnwwfHx8fDE3OTIyMDExNDN8MA&ixlib=rb-4.1.0&q=85",
  residential: "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxob3VzZWtlZXBlciUyMGx1eHVyeSUyMGludGVyaW9yJTIwY2xlYW5pbmd8ZW58MHx8fHwxNzkyMjAwNTgwfDA&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMG9mZmljZSUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MjIwMDU3M3ww&ixlib=rb-4.1.0&q=85",
  skyline: "https://images.unsplash.com/photo-1595125989588-36d745a2a828?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxhdWNrbGFuZCUyMGNpdHklMjBza3lsaW5lJTIwc3Vuc2V0fGVufDB8fHx8MTc5MjIwMDU4MHww&ixlib=rb-4.1.0&q=85",
  detail: "https://images.pexels.com/photos/6466489/pexels-photo-6466489.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  minimal: "https://images.pexels.com/photos/29012619/pexels-photo-29012619.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-gold-light/60 blur-3xl" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 pt-14 lg:grid-cols-12 lg:px-10 lg:pb-28 lg:pt-20">
        <motion.div className="lg:col-span-7" style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="rule-gold w-12" />
            <Overline>Auckland's Premium Cleaning Atelier</Overline>
          </motion.div>

          <h1 className="mt-7 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Immaculate spaces,</MaskedLine>
            <MaskedLine delay={0.4}>
              <em className="text-gold-gradient not-italic lg:italic">golden</em> standard
            </MaskedLine>
            <MaskedLine delay={0.55}>of care.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            className="mt-7 max-w-xl text-sm leading-relaxed text-stone sm:text-base"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          >
            TidyCare brings five-star hotel housekeeping to Auckland's finest
            homes and workplaces — vetted specialists, eco-conscious products,
            and a finish that sparkles down to the last detail.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-5"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          >
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="btn-gold rounded-full px-8 py-4 text-xs font-bold uppercase tracking-[0.2em]"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="btn-ghost-gold group flex items-center gap-2 rounded-full border border-ink/15 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ink"
            >
              Explore Services
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div
            className="mt-12 flex items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
          >
            <div className="flex text-gold" data-testid="hero-rating-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={15} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <p className="text-xs tracking-wide text-stone">
              <span className="font-semibold text-ink">4.9 / 5</span> from 200+ Auckland clients
            </p>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 0.5, ease: EASE }}
            className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-gold-champagne/40 shadow-[0_40px_80px_-30px_rgba(154,123,56,0.45)]"
          >
            <motion.img
              src={IMG.hero}
              alt="Pristine modern Auckland office space maintained by TidyCare"
              data-testid="hero-image"
              className="h-[26rem] w-full object-cover sm:h-[30rem] lg:h-[34rem]"
              style={{ y: imgY, scale: 1.15 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            data-testid="hero-badge"
            className="absolute -bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-4 rounded-2xl border border-gold-champagne/50 bg-white/90 px-6 py-4 shadow-xl backdrop-blur-md lg:-left-10 lg:translate-x-0"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          >
            <Sparkle className="h-7 w-7 text-gold" />
            <div>
              <p className="font-serif text-2xl font-semibold text-ink">500+</p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-stone">Spaces Perfected</p>
            </div>
          </motion.div>

          <motion.div
            className="absolute -right-4 top-10 hidden text-gold lg:block"
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
          >
            <Sparkle className="h-9 w-9" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => {
  const items = [
    "Residential Sanctuary",
    "Commercial Excellence",
    "Auckland Wide",
    "Five-Star Standards",
    "Eco-Conscious Products",
    "Vetted Specialists",
  ];
  const row = [...items, ...items];
  return (
    <div data-testid="marquee-band" className="overflow-hidden border-y border-gold/20 bg-obsidian py-5">
      <div className="animate-marquee flex w-max items-center">
        {row.map((item, i) => (
          <div key={i} className="flex items-center">
            <span className="whitespace-nowrap px-8 font-serif text-lg italic text-ivory/85">{item}</span>
            <Sparkle className="h-3.5 w-3.5 shrink-0 text-gold" />
          </div>
        ))}
      </div>
    </div>
  );
};

const SERVICES = [
  {
    title: "Residential Estates",
    desc: "Weekly, fortnightly or one-off cleans tailored to your home's rhythm — kitchens, bathrooms, living spaces perfected.",
    img: IMG.residential,
    span: "lg:col-span-7",
    testid: "bento-residential",
  },
  {
    title: "Office & Commercial",
    desc: "Discreet after-hours cleaning for executive suites, studios and retail floors.",
    img: IMG.office,
    span: "lg:col-span-5",
    testid: "bento-commercial",
  },
  {
    title: "Deep Sanitisation",
    desc: "A meticulous top-to-bottom reset using eco-conscious, hospital-grade products.",
    img: IMG.detail,
    span: "lg:col-span-5",
    testid: "bento-deep",
  },
  {
    title: "Move In / Move Out",
    desc: "Bond-ready end-of-tenancy cleans that pass the sharpest property inspection.",
    img: IMG.minimal,
    span: "lg:col-span-7",
    testid: "bento-move",
  },
];

const BentoServices = () => (
  <section data-testid="services-bento" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
    <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <div>
        <Reveal><Overline>What We Perfect</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Care for every corner<br />of your world
          </h2>
        </Reveal>
      </div>
      <Reveal delay={0.2}>
        <Link
          to="/services"
          data-testid="bento-view-all-btn"
          className="group flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-gold-dark"
        >
          View all services
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>

    <div className="grid gap-6 lg:grid-cols-12">
      {SERVICES.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.08} className={s.span}>
          <Link to="/services" data-testid={s.testid} className="group relative block h-72 overflow-hidden rounded-3xl lg:h-80">
            <img
              src={s.img}
              alt={s.title}
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-obsidian/20 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-7">
              <h3 className="font-serif text-2xl font-medium text-ivory">{s.title}</h3>
              <p className="mt-2 max-w-md text-sm text-ivory/70">{s.desc}</p>
            </div>
            <div className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-ivory/30 bg-obsidian/30 text-ivory backdrop-blur-sm transition-colors duration-300 group-hover:bg-gold group-hover:text-obsidian">
              <ArrowRight size={16} />
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const STATS = [
  { value: "500+", label: "Homes Perfected" },
  { value: "120+", label: "Offices Maintained" },
  { value: "4.9", label: "Average Rating" },
  { value: "100%", label: "Vetted & Insured" },
];

const StatsBand = () => (
  <section data-testid="stats-band" className="bg-obsidian py-20 lg:py-24">
    <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 lg:grid-cols-4 lg:px-10">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1} className="text-center">
          <p className="font-serif text-4xl font-medium text-gold-gradient lg:text-5xl" data-testid={`stat-${i}`}>
            {s.value}
          </p>
          <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ivory/50">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const TESTIMONIALS = [
  {
    quote: "TidyCare transformed our Remuera villa. The attention to detail is unlike anything we've experienced — it genuinely feels like a five-star hotel every Friday.",
    name: "Charlotte M.",
    place: "Remuera",
  },
  {
    quote: "Our Ponsonby studio has never looked sharper. The team is discreet, punctual and thorough. Clients comment on the space constantly.",
    name: "James T.",
    place: "Ponsonby",
  },
  {
    quote: "The end-of-tenancy clean was flawless — full bond returned without a single query. Worth every cent.",
    name: "Priya & Sam K.",
    place: "Takapuna",
  },
  {
    quote: "TidyCare keeps our showroom gleaming — the glass, the floors, every detail perfect before we open each morning. Our customers notice it, and so do we.",
    name: "Madhawa S.",
    place: "Sterling Cars",
  },
];

const Testimonials = () => (
  <section data-testid="testimonials-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
    <Reveal className="text-center">
      <Overline>Word of Mouth</Overline>
      <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl lg:text-5xl">
        Auckland trusts TidyCare
      </h2>
    </Reveal>
    <div className="mt-14 grid gap-6 md:grid-cols-2">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={t.name} delay={i * 0.12}>
          <figure
            data-testid={`testimonial-${i}`}
            className="flex h-full flex-col rounded-3xl border border-sand bg-white p-8 transition-shadow duration-500 hover:shadow-[0_24px_50px_-24px_rgba(154,123,56,0.35)]"
          >
            <div className="flex text-gold">
              {[...Array(5)].map((_, s) => (
                <Star key={s} size={14} fill="currentColor" strokeWidth={0} />
              ))}
            </div>
            <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-ink/85">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-6 border-t border-sand pt-5">
              <p className="text-sm font-semibold text-ink">{t.name}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-gold-dark">{t.place}, Auckland</p>
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

const CtaBand = () => (
  <section data-testid="cta-band" className="relative overflow-hidden">
    <img src={IMG.skyline} alt="Auckland skyline at dusk" className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-obsidian/75" />
    <div className="relative mx-auto max-w-4xl px-6 py-28 text-center lg:py-36">
      <Reveal>
        <Sparkle className="mx-auto h-8 w-8 text-gold" />
        <h2 className="mt-6 font-serif text-3xl font-medium tracking-tight text-ivory sm:text-4xl lg:text-5xl">
          Ready for the <em className="text-gold-gradient not-italic">golden</em> touch?
        </h2>
        <p className="mx-auto mt-5 max-w-lg text-sm leading-relaxed text-ivory/70 sm:text-base">
          Tell us about your space and we'll craft a tailored quote within one
          business day — no obligation, no pressure.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-5">
          <Link
            to="/contact"
            data-testid="cta-quote-btn"
            className="btn-gold rounded-full px-9 py-4 text-xs font-bold uppercase tracking-[0.2em]"
          >
            Get a Free Quote
          </Link>
          <span className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ivory/60">
            <ShieldCheck size={15} className="text-gold" /> Fully insured
            <Leaf size={15} className="ml-3 text-gold" /> Eco-conscious
          </span>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <motion.main
      data-testid="home-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
    >
      <Hero />
      <Marquee />
      <BentoServices />
      <StatsBand />
      <Testimonials />
      <CtaBand />
    </motion.main>
  );
}
